import React from 'react';
import { definePage, defineResource, ListQuery } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import NewsForm from 'src/components/Storefront/NewsForm';
import { useTenantContext } from 'src/context/useTenantContext';
import {
  tenantStorefrontPostCreate,
  tenantStorefrontPostDelete,
  tenantStorefrontPostUpdate,
  tenantStorefrontPosts,
} from 'src/services/apis/tenantRequests';
import { RestaurantScope } from 'src/services/apis/tenantRequests';
import { StorefrontPostEntity } from 'src/services/apis/types';
import { QUERIES } from 'src/utils/react-query';

/**
 * Mirrors `StorefrontPostStatusEnum` in `apps/server/src/storefront/storefront.enum.ts`.
 *
 * A copy, not an import: the portal cannot reach into the server's source, and the generated
 * client types are no substitute — Swagger publishes this enum as the string union
 * `'DRAFT' | 'PUBLISHED'` while the column is numeric and the API stores/expects `1`/`2`.
 * Sending the string makes the server's `@IsIn([1, 2])` validation reject the write; reading
 * it back gives a number. The same duplication the Zalo client documents for order statuses.
 * Adding a member here is an append on both sides, never a migration.
 */
export const STOREFRONT_POST_STATUS = { DRAFT: 1, PUBLISHED: 2 } as const;

/**
 * The `status` cell's tag map, keyed by the numeric value the API returns.
 */
export const STOREFRONT_POST_STATUS_TAG_MAP: Record<string, { labelKey: string; type: string }> = {
  [STOREFRONT_POST_STATUS.DRAFT]: { labelKey: 'storefront.news.statusDraft', type: 'gray' },
  [STOREFRONT_POST_STATUS.PUBLISHED]: { labelKey: 'storefront.news.statusPublished', type: 'green' },
};

const storefrontNews = defineResource({
  key: QUERIES.STOREFRONT_POSTS,
  api: {
    list: (scope: RestaurantScope, query?: ListQuery) => tenantStorefrontPosts(scope, query),
    create: tenantStorefrontPostCreate,
    update: tenantStorefrontPostUpdate,
    remove: tenantStorefrontPostDelete,
  },
  // A storefront has tens of posts, not thousands, and its list has no server-side search
  // configured in @nestjsx/crud beyond the `s` param — client search over the fetched set
  // is honest about that, whereas server search would silently only ever match page one.
  search: 'client',
  searchFields: ['title', 'slug'],
  filterable: [
    {
      field: 'status',
      labelKey: 'storefront.news.status',
      type: 'enum',
      options: [
        { id: String(STOREFRONT_POST_STATUS.DRAFT), labelKey: 'storefront.news.statusDraft' },
        { id: String(STOREFRONT_POST_STATUS.PUBLISHED), labelKey: 'storefront.news.statusPublished' },
      ],
    },
  ],
});

/**
 * The News page config, as a hook to match the pilot's shape. The resource is module-scope
 * because nothing in it is per-tenant: the tenant scope travels as the `scope` argument
 * `useCrudResource` supplies (and is appended to the query key there), so this hook reads the
 * context only to re-render the page when the tenant changes.
 */
export const useStorefrontNewsPage = () => {
  useTenantContext();

  return {
    page: definePage<StorefrontPostEntity, any, any, RestaurantScope, any>(storefrontNews, {
      titleKey: 'storefront.news.title',
      // The storefront layout's h1 already names the page.
      hideTitle: true,
      descriptionKey: 'storefront.news.description',
      createLabelKey: 'storefront.news.create',
      updateLabelKey: 'storefront.news.update',
      deleteHeadingKey: 'storefront.news.deleteHeading',
      columns: [
        // The column a post is edited by must survive every prefs reconciliation.
        { key: 'title', headerKey: 'common.title', sortable: true, alwaysVisible: true },
        // Both the public address and the uniqueness key, so it may never be hidden.
        { key: 'slug', headerKey: 'common.slug', alwaysVisible: true },
        // `tag`, not an invented 'enum' kind: columnDisplays.tsx exposes text / currency /
        // number / date / boolean / tag / reference, and the tag map is what carries the
        // numeric status to its two localized labels.
        {
          key: 'status',
          headerKey: 'storefront.news.status',
          display: { kind: 'tag', map: STOREFRONT_POST_STATUS_TAG_MAP },
        },
        { key: 'publishedAt', headerKey: 'storefront.news.publishedAt', display: { kind: 'date' } },
        { key: 'updatedAt', headerKey: 'common.updatedAt', display: { kind: 'date' } },
      ],
      form: NewsForm,
      toPayload: (values: any) => ({
        ...values,
        // The API stores an int enum; a Carbon Select hands back a string.
        status: Number(values.status),
        publishedAt: values.publishedAt || null,
        coverFileId: values.coverFileId || null,
      }),
      emptyState: <EmptyState />,
    }),
  };
};
